import { FC, useEffect, useState } from 'react';
import { Button, ButtonGroup } from '@chakra-ui/react';
import { useSplunkActivityLogger, useSplunkLogger } from '@resolutionlife/ui';
import { isEmpty } from 'ramda';

import {
  Operation,
  Role,
  TreatyUserDetailFragment,
  useUpdateUserRolesMutation,
} from '../../../../graphql/generated';
import { GetTreatyUserDocument } from '../../../../graphql/generated';
import { usePolling } from '../../../../hooks/use-polling/use-polling';
import { createRolePermissionsMap } from '../../../../utils/role-utils/create-role-permissions-map';
import { byAlphabeticalLabel } from '../../../../utils/sorting/sorting';
import { MultiCombobox, Option } from '../../../multi-combobox/multi-combobox';
import { Border, ReadOnlyView } from './../read-only-view';

type EditableUserRolesProps = {
  treatyUser: TreatyUserDetailFragment;
  roles: Role[];
  startPolling: (pollInterval: number) => void;
  stopPolling: () => void;
  /** When false, the roles are shown read-only with no Edit button. Defaults to true. */
  canEdit?: boolean;
};

export const EditableUserRoles: FC<EditableUserRolesProps> = ({
  treatyUser,
  roles,
  startPolling,
  stopPolling,
  canEdit = true,
}) => {
  const [selectedRoles, setSelectedRoles] = useState<string[]>(
    treatyUser.roles
  );
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isEditable, setIsEditable] = useState<boolean>(false);

  const [showSaved, setShowSaved] = useState<boolean>(false);
  const [showFailed, setShowFailed] = useState<boolean>(false);

  const [shouldPoll, setShouldPoll] = useState<boolean>(false);

  useEffect(() => {
    resetMessages();
  }, [treatyUser]);

  usePolling({
    startPolling,
    stopPolling,
    shouldPoll,
    interval: 300,
    onStop: () => {
      toggleEdit();
      setShowSaved(true);
    },
  });

  useEffect(() => {
    const checkIfSameRoles = (roles1: string[], roles2: string[]) => {
      const sortedRoles1 = [...roles1];
      sortedRoles1.sort((a, b) => a.localeCompare(b));
      const rolesStr1 = sortedRoles1.join();
      const sortedRoles2 = [...roles2];
      sortedRoles2.sort((a, b) => a.localeCompare(b));
      const rolesStr2 = sortedRoles2.join();

      return rolesStr1 === rolesStr2;
    };

    // Check if the roles from the API and the locally selected roles are the same
    const areSameRoles = checkIfSameRoles(treatyUser.roles, selectedRoles);

    // Poll until the roles are updated
    if (shouldPoll && !areSameRoles) {
      return setShouldPoll(true);
    }

    // Stop polling when the roles have updated
    if (areSameRoles) {
      return setShouldPoll(false);
    }
  }, [selectedRoles, shouldPoll, treatyUser.roles]);

  const [updateUserRoles, { loading }] = useUpdateUserRolesMutation({
    refetchQueries: [GetTreatyUserDocument],
  });
  const activityLogger = useSplunkActivityLogger();
  const splunkLogger = useSplunkLogger();

  const rolePermissions = createRolePermissionsMap(roles);

  const options = roles
    ? roles.map((role) => ({
        value: role.id,
        label: role.id,
        info: rolePermissions[role.id],
      }))
    : [];

  const bySelected = (option: Option) => selectedRoles.includes(option.value);
  const selectedOptions = options.filter(bySelected);

  const infoTags = treatyUser.roles.map((role) => ({
    label: role,
    info: rolePermissions[role],
  }));

  const resetMessages = () => {
    setIsValid(true);
    setShowSaved(false);
    setShowFailed(false);
  };

  const handleRolesChange = (selectedOptions: Option[]) => {
    const roles = selectedOptions.map((option) => option.value);
    setSelectedRoles(roles);
    resetMessages();
  };

  const toggleEdit = () => setIsEditable(!isEditable);

  const cancel = () => {
    setSelectedRoles(treatyUser.roles);
    resetMessages();
    toggleEdit();
  };

  const handleUpdateUserRoles = async () => {
    if (isEmpty(selectedOptions)) {
      setIsValid(false);
      return;
    }

    try {
      await updateUserRoles({
        variables: {
          input: {
            userId: treatyUser.id,
            op: Operation.SET,
            roles: selectedRoles,
          },
        },
      });
      activityLogger.logUserModify({
        actionType: 'modify_roles',
        changeDescription: `Roles updated to: ${selectedRoles.join(', ')}`,
      });
      setShouldPoll(true);
    } catch {
      setShowFailed(true);
      splunkLogger.error('User role update failed', { eventCategory: 'user_management_error', userId: treatyUser.id, attemptedRoles: selectedRoles.join(', ') });
      toggleEdit();
    }
  };

  if (!isEditable) {
    const sortedInfoTags = [...infoTags];
    sortedInfoTags.sort(byAlphabeticalLabel);
    return (
      <ReadOnlyView
        label="Assigned Roles"
        tags={sortedInfoTags}
        onEditClick={toggleEdit}
        showSaved={showSaved}
        showFailed={showFailed}
        canEdit={canEdit}
      />
    );
  }

  const sortedOptions = [...options];
  sortedOptions.sort(byAlphabeticalLabel);
  const sortedSelectedOptions = [...selectedOptions];
  sortedSelectedOptions.sort(byAlphabeticalLabel);

  return (
    <Border spacing={4}>
      <MultiCombobox
        label="Assigned Roles"
        options={sortedOptions}
        selectedOptions={sortedSelectedOptions}
        onChange={handleRolesChange}
        isInvalid={!isValid}
        errorMessage="Please assign at least one role"
        testId="roles-multi-combobox"
        sx={{
          // prevents the label from moving when the view switches from read-only to editable
          label: {
            display: 'flex',
            alignItems: 'center',
            fontWeight: 600,
            minHeight: 10,
          },
        }}
      />
      <ButtonGroup colorScheme="blue">
        <Button
          onClick={handleUpdateUserRoles}
          isLoading={loading || shouldPoll}
          data-testid="save-button"
        >
          Save
        </Button>
        <Button onClick={cancel} variant="ghost">
          Cancel
        </Button>
      </ButtonGroup>
    </Border>
  );
};
